// Wie eet mee (spec E1): one day's meal, from Vandaag, the Week day or the evening planning.
import { useState, type FormEvent } from "react";
import { Check, Pencil, Plus, Utensils } from "lucide-react";
import { toast } from "sonner";
import { mealLine } from "@shared/meals";
import { displayDate } from "@shared/time";
import { savePerson, setMeal, useMeal, usePeople } from "../lib/meals";
import { Sheet } from "./Sheet";
import { Button, cn } from "./ui";

/** The line under the evening meal block. Tapping it changes who eats along, unless the day is over. */
/** `inBlock`: under the cooking block, which already reads "Uit eten" then; elsewhere (evening step 2) the line says it. */
export function MealLine({ date, editable, inBlock = true }: { date: string; editable: boolean; inBlock?: boolean }) {
  const view = useMeal(date);
  const [open, setOpen] = useState(false);
  if (!view) return null;
  const names = view.people.map((p) => p.name);
  const line = mealLine(view.place, names);
  // Uit eten: the block itself says so already; the line shows who comes along, or only the way to change it.
  const shown = view.place === "out" && inBlock ? (names.length ? mealLine("home", names) : "Wijzigen") : line;
  if (!editable) return view.place === "out" && !names.length ? null : <p className="mt-1 px-2 text-sm text-ink-soft">{shown}</p>;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Wie eet mee: ${line}. Wijzigen`}
        className="mt-1 inline-flex min-h-11 items-center gap-2 rounded-xl px-2 text-ink-soft"
      >
        <Utensils aria-hidden className="size-4 text-pine" />
        <span className="font-semibold text-ink">{shown}</span>
        <Pencil aria-hidden className="size-4" />
      </button>
      {open && <MealSheet date={date} onClose={() => setOpen(false)} />}
    </>
  );
}

export function MealSheet({ date, onClose }: { date: string; onClose: () => void }) {
  const view = useMeal(date);
  const people = usePeople();
  if (!view || !people) return null;
  return <MealForm date={date} initialPlace={view.place} initialIds={view.people.map((p) => p.id)} people={people} onClose={onClose} />;
}

function MealForm({
  date,
  initialPlace,
  initialIds,
  people,
  onClose,
}: {
  date: string;
  initialPlace: "home" | "out";
  initialIds: string[];
  people: { id: string; name: string }[];
  onClose: () => void;
}) {
  const [place, setPlace] = useState(initialPlace);
  const [ids, setIds] = useState(initialIds);
  const [newName, setNewName] = useState("");
  const toggle = (id: string) => setIds((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  async function addPerson(e: FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    // A name that is there already is that person, not a second one.
    const known = people.find((p) => p.name.trim().toLowerCase() === newName.trim().toLowerCase());
    const id = known?.id ?? (await savePerson(null, newName, people.length));
    setIds((list) => (list.includes(id) ? list : [...list, id]));
    setNewName("");
  }

  async function save() {
    await setMeal(date, place, ids);
    toast(place === "out" ? "Uit eten" : "Wie eet mee is bewaard");
    onClose();
  }

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()} title="Wie eet mee" description={displayDate(date)}>
      <div role="radiogroup" aria-label="Waar" className="mb-4 grid grid-cols-2 gap-2">
        {(
          [
            ["home", "Thuis"],
            ["out", "Uit eten"],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={place === value}
            onClick={() => setPlace(value)}
            className={cn(
              "min-h-12 rounded-xl border-2 font-semibold",
              place === value ? "border-pine bg-pine text-paper" : "border-mist bg-paper text-ink",
            )}
          >
            {label}
          </button>
        ))}
      </div>
      {place === "out" && (
        <p className="mb-4 text-sm text-ink-soft">Het kookblok wordt Uit eten. Wil je een tijd en plek, zet het dan in Afspraken.</p>
      )}
      <fieldset className="mb-4">
        <legend className="mb-1 font-semibold">Wie eet er mee</legend>
        <ul className="divide-y divide-mist rounded-2xl border border-mist bg-paper">
          {people.map((p) => {
            const on = ids.includes(p.id);
            return (
              <li key={p.id}>
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={on}
                  onClick={() => toggle(p.id)}
                  className="flex min-h-12 w-full items-center gap-3 px-3 text-left"
                >
                  <span
                    aria-hidden
                    className={cn(
                      "flex size-7 shrink-0 items-center justify-center rounded-lg border-2",
                      on ? "border-pine bg-pine text-paper" : "border-ink-soft",
                    )}
                  >
                    {on && <Check className="size-4" strokeWidth={3} />}
                  </span>
                  <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{p.name}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </fieldset>
      <form onSubmit={addPerson} className="mb-5 flex items-end gap-2">
        <label className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-sm text-ink-soft">Iemand anders</span>
          <input
            value={newName}
            maxLength={80}
            onChange={(e) => setNewName(e.target.value)}
            className="min-h-12 rounded-xl border-2 border-mist bg-paper px-3 text-ink"
          />
        </label>
        <Button type="submit" variant="secondary" aria-label="Persoon toevoegen">
          <Plus aria-hidden className="size-5" />
          Toevoegen
        </Button>
      </form>
      <Button className="w-full" onClick={save}>
        Opslaan
      </Button>
    </Sheet>
  );
}
